/**
 * Wd Order Shipping Geolocations Service (wd-order-shipping-geolocations.service.ts).
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.66.0 OAUI-665 Order Details shows unexpected "Ecommerce" in label
 * 4.66.0 OAUI-662 Refactor usages of WD_DELIVERABLE_COUNTRIES and initialize based on default
 * 4.64.0 OAUI-646 Upgrades for Angular v16
 * 4.46.0 OAUI-572 Remove references to order-admin-api
 * 4.41.0 OAUI-549 Add shipping info to order details v2
 *
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WdLocalEnvironmentService } from '../../../environment/wd-local-environment.service';

@Injectable({
  providedIn: 'root',
})
export class WdOrderShippingGeolocationsService {
  // Booleans
  isLoading = new BehaviorSubject(false);

  // Default and deliverable countries based on the constant [WD_DELIVERABLE_COUNTRIES]
  defaultCountryCode: any = null;
  filteredCountries: Array<string> = [];

  constructor(
    private environment: WdLocalEnvironmentService,
    @Inject('wdErrorService') public wdErrorService: any,
  ) {
    // Parse string constant to array
    this.filteredCountries = JSON.parse(
      this.environment.constants.getDeliverableCountries,
    );
  }

  /**
   * Process filter countries into codes and default country
   *
   * @since 4.66.0
   * @param filterCountries
   */
  private processFilterCountries(filterCountries) {
    let defaultCountry = null;
    const filteredCountryCodes = [];

    if (filterCountries) {
      filterCountries.forEach((item) => {
        // Add code for filtering
        filteredCountryCodes.push(item.code);
        // Check if this is the default
        if (item.default === true) {
          defaultCountry = item;
        }
      });
    }

    return { defaultCountry, filteredCountryCodes };
  }

  /**
   * GET geolocation options
   *
   * @since 4.41.0
   */
  async getGeolocationOptions(): Promise<any> {
    // Toggle Loader
    this.isLoading.next(true);
    try {
      // @todo Previously hard coded in the wd-library-style-guide, move to API
      const options = [
        {
          id: 'CA',
          label: 'Canada',
          children: [
            {
              id: 'AB',
              label: 'Alberta',
            },
            {
              id: 'BC',
              label: 'British Columbia',
            },
            {
              id: 'MB',
              label: 'Manitoba',
            },
            {
              id: 'NB',
              label: 'New Brunswick',
            },
            {
              id: 'NL',
              label: 'Newfoundland and Labrador',
            },
            {
              id: 'NT',
              label: 'Northwest Territories',
            },
            {
              id: 'NS',
              label: 'Nova Scotia',
            },
            {
              id: 'NU',
              label: 'Nunavut',
            },
            {
              id: 'PE',
              label: 'Prince Edward Island',
            },
            {
              id: 'ON',
              label: 'Ontario',
            },
            {
              id: 'QC',
              label: 'Quebec',
            },
            {
              id: 'SK',
              label: 'Saskatchewan',
            },
            {
              id: 'YT',
              label: 'Yukon',
            },
          ],
        },
        {
          id: 'GB',
          label: 'United Kingdom',
          children: [
            {
              label: 'Avon',
              id: 'AVN',
            },
            {
              label: 'Bedfordshire',
              id: 'BDF',
            },
            {
              label: 'Berkshire',
              id: 'BRK',
            },
            {
              label: 'Bristol, City of',
              id: 'COB',
            },
            {
              label: 'Buckinghamshire',
              id: 'BKM',
            },
            {
              label: 'Cambridgeshire',
              id: 'CAM',
            },
            {
              label: 'Cheshire',
              id: 'CHS',
            },
            {
              label: 'Cleveland',
              id: 'CLV',
            },
            {
              label: 'Cornwall',
              id: 'CON',
            },
            {
              label: 'Cumbria',
              id: 'CMA',
            },
            {
              label: 'Derbyshire',
              id: 'DBY',
            },
            {
              label: 'Devon',
              id: 'DEV',
            },
            {
              label: 'Dorset',
              id: 'DOR',
            },
            {
              label: 'Durham',
              id: 'DUR',
            },
            {
              label: 'East Sussex',
              id: 'SXE',
            },
            {
              label: 'Essex',
              id: 'ESS',
            },
            {
              label: 'Gloucestershire',
              id: 'GLS',
            },
            {
              label: 'Greater London',
              id: 'LND',
            },
            {
              label: 'Greater Manchester',
              id: 'GTM',
            },
            {
              label: 'Hampshire (County of Southampton)',
              id: 'HAM',
            },
            {
              label: 'Hereford and Worcester',
              id: 'HWR',
            },
            {
              label: 'Herefordshire',
              id: 'HEF',
            },
            {
              label: 'Hertfordshire',
              id: 'HRT',
            },
            {
              label: 'Isle of Wight',
              id: 'IOW',
            },
            {
              label: 'Kent',
              id: 'KEN',
            },
            {
              label: 'Lancashire',
              id: 'LAN',
            },
            {
              label: 'Leicestershire',
              id: 'LEI',
            },
            {
              label: 'Lincolnshire',
              id: 'LIN',
            },
            {
              label: 'London',
              id: 'LDN',
            },
            {
              label: 'Merseyside',
              id: 'MSY',
            },
            {
              label: 'Middlesex',
              id: 'MDX',
            },
            {
              label: 'Norfolk',
              id: 'NFK',
            },
            {
              label: 'Northamptonshire',
              id: 'NTH',
            },
            {
              label: 'Northumberland',
              id: 'NBL',
            },
            {
              label: 'North Humberside',
              id: 'NHM',
            },
            {
              label: 'North Yorkshire',
              id: 'NYK',
            },
            {
              label: 'Nottinghamshire',
              id: 'NTT',
            },
            {
              label: 'Oxfordshire',
              id: 'OXF',
            },
            {
              label: 'Rutland',
              id: 'RUT',
            },
            {
              label: 'Shropshire',
              id: 'SAL',
            },
            {
              label: 'Somerset',
              id: 'SOM',
            },
            {
              label: 'South Humberside',
              id: 'SHM',
            },
            {
              label: 'South Yorkshire',
              id: 'SYK',
            },
            {
              label: 'Staffordshire',
              id: 'STS',
            },
            {
              label: 'Suffolk',
              id: 'SFK',
            },
            {
              label: 'Surrey',
              id: 'SRY',
            },
            {
              label: 'Tyne and Wear',
              id: 'TWR',
            },
            {
              label: 'Warwickshire',
              id: 'WAR',
            },
            {
              label: 'West Midlands',
              id: 'WMD',
            },
            {
              label: 'West Sussex',
              id: 'SXW',
            },
            {
              label: 'West Yorkshire',
              id: 'WYK',
            },
            {
              label: 'Wiltshire',
              id: 'WIL',
            },
            {
              label: 'Worcestershire',
              id: 'WOR',
            },
            {
              label: 'Antrim',
              id: 'ANT',
            },
            {
              label: 'Armagh',
              id: 'ARM',
            },
            {
              label: 'Belfast, City of',
              id: 'BLF',
            },
            {
              label: 'Down',
              id: 'DOW',
            },
            {
              label: 'Fermanagh',
              id: 'FER',
            },
            {
              label: 'Londonderry',
              id: 'LDY',
            },
            {
              label: 'Derry, City of',
              id: 'DRY',
            },
            {
              label: 'Tyrone',
              id: 'TYR',
            },
            {
              label: 'Aberdeen, City of',
              id: 'AN',
            },
            {
              label: 'Aberdeenshire',
              id: 'ABD',
            },
            {
              label: 'Angus (Forfarshire)',
              id: 'ANS',
            },
            {
              label: 'Argyll',
              id: 'AGB',
            },
            {
              label: 'Ayrshire',
              id: 'ARG',
            },
            {
              label: 'Banffshire',
              id: 'BAN',
            },
            {
              label: 'Berwickshire',
              id: 'BEW',
            },
            {
              label: 'Bute',
              id: 'BUT',
            },
            {
              label: 'Caithness',
              id: 'CAI',
            },
            {
              label: 'Clackmannanshire',
              id: 'CLK',
            },
            {
              label: 'Cromartyshire',
              id: 'COC',
            },
            {
              label: 'Dumfriesshire',
              id: 'DFS',
            },
            {
              label: 'Dunbartonshire (Dumbarton)',
              id: 'DNB',
            },
            {
              label: 'Dundee, City of',
              id: 'DD',
            },
            {
              label: 'East Lothian (Haddingtonshire)',
              id: 'ELN',
            },
            {
              label: 'Edinburgh, City of',
              id: 'EB',
            },
            {
              label: 'Fife',
              id: 'FIF',
            },
            {
              label: 'Glasgow, City of',
              id: 'GLA',
            },
            {
              label: 'Inverness-shire',
              id: 'INV',
            },
            {
              label: 'Kincardineshire',
              id: 'KCD',
            },
            {
              label: 'Kinross-shire',
              id: 'KRS',
            },
            {
              label: 'Kirkcudbrightshire',
              id: 'KKD',
            },
            {
              label: 'Lanarkshire',
              id: 'LKS',
            },
            {
              label: 'Midlothian (County of Edinburgh)',
              id: 'MLN',
            },
            {
              label: 'Moray (Elginshire)',
              id: 'MOR',
            },
            {
              label: 'Nairnshire',
              id: 'NAI',
            },
            {
              label: 'Orkney',
              id: 'OKI',
            },
            {
              label: 'Peeblesshire',
              id: 'PEE',
            },
            {
              label: 'Perthshire',
              id: 'PER',
            },
            {
              label: 'Renfrewshire',
              id: 'RFW',
            },
            {
              label: 'Ross and Cromarty',
              id: 'ROC',
            },
            {
              label: 'Ross-shire',
              id: 'ROS',
            },
            {
              label: 'Roxburghshire',
              id: 'ROX',
            },
            {
              label: 'Selkirkshire',
              id: 'SEL',
            },
            {
              label: 'Shetland (Zetland)',
              id: 'SHI',
            },
            {
              label: 'Stirlingshire',
              id: 'STI',
            },
            {
              label: 'Sutherland',
              id: 'SUT',
            },
            {
              label: 'West Lothian (Linlithgowshire)',
              id: 'WLN',
            },
            {
              label: 'Wigtownshire',
              id: 'WIG',
            },
            {
              label: 'Clwyd',
              id: 'CWD',
            },
            {
              label: 'Dyfed',
              id: 'DFD',
            },
            {
              label: 'Gwent',
              id: 'GNT',
            },
            {
              label: 'Gwynedd',
              id: 'GWN',
            },
            {
              label: 'Mid Glamorgan',
              id: 'MGM',
            },
            {
              label: 'Powys',
              id: 'POW',
            },
            {
              label: 'South Glamorgan',
              id: 'SGM',
            },
            {
              label: 'West Glamorgan',
              id: 'WGM',
            },
          ],
        },
        {
          id: 'US',
          label: 'United States',
          children: [
            {
              id: 'AL',
              label: 'Alabama',
            },
            {
              id: 'AK',
              label: 'Alaska',
            },
            {
              id: 'AZ',
              label: 'Arizona',
            },
            {
              id: 'AR',
              label: 'Arkansas',
            },
            {
              id: 'CA',
              label: 'California',
            },
            {
              id: 'CO',
              label: 'Colorado',
            },
            {
              id: 'CT',
              label: 'Connecticut',
            },
            {
              id: 'DE',
              label: 'Delaware',
            },
            {
              id: 'DC',
              label: 'District of Columbia',
            },
            {
              id: 'FL',
              label: 'Florida',
            },
            {
              id: 'GA',
              label: 'Georgia',
            },
            {
              id: 'IH',
              label: 'Hawaii',
            },
            {
              id: 'ID',
              label: 'Idaho',
            },
            {
              id: 'IL',
              label: 'Illinois',
            },
            {
              id: 'IN',
              label: 'Indiana',
            },
            {
              id: 'IA',
              label: 'Iowa',
            },
            {
              id: 'KS',
              label: 'Kansas',
            },
            {
              id: 'KY',
              label: 'Kentucky',
            },
            {
              id: 'LA',
              label: 'Louisiana',
            },
            {
              id: 'ME',
              label: 'Maine',
            },
            {
              id: 'MT',
              label: 'Montana',
            },
            {
              id: 'NE',
              label: 'Nebraska',
            },
            {
              id: 'NV',
              label: 'Nevada',
            },
            {
              id: 'NH',
              label: 'New Hampshire',
            },
            {
              id: 'NJ',
              label: 'New Jersey',
            },
            {
              id: 'NM',
              label: 'New Mexico',
            },
            {
              id: 'NY',
              label: 'New York',
            },
            {
              id: 'NC',
              label: 'North Carolina',
            },
            {
              id: 'ND',
              label: 'North Dakota',
            },
            {
              id: 'OH',
              label: 'Ohio',
            },
            {
              id: 'OK',
              label: 'Oklahoma',
            },
            {
              id: 'OR',
              label: 'Oregon',
            },
            {
              id: 'MD',
              label: 'Maryland',
            },
            {
              id: 'MA',
              label: 'Massachusetts',
            },
            {
              id: 'MI',
              label: 'Michigan',
            },
            {
              id: 'MN',
              label: 'Minnesota',
            },
            {
              id: 'MS',
              label: 'Mississippi',
            },
            {
              id: 'MO',
              label: 'Missouri',
            },
            {
              id: 'PA',
              label: 'Pennsylvania',
            },
            {
              id: 'RI',
              label: 'Rhode Island',
            },
            {
              id: 'SC',
              label: 'South Carolina',
            },
            {
              id: 'SD',
              label: 'South Dakota',
            },
            {
              id: 'TN',
              label: 'Tennessee',
            },
            {
              id: 'TX',
              label: 'Texas',
            },
            {
              id: 'UT',
              label: 'Utah',
            },
            {
              id: 'VT',
              label: 'Vermont',
            },
            {
              id: 'VA',
              label: 'Virginia',
            },
            {
              id: 'WA',
              label: 'Washington',
            },
            {
              id: 'WV',
              label: 'West Virginia',
            },
            {
              id: 'WI',
              label: 'Wisconsin',
            },
            {
              id: 'WY',
              label: 'Wyoming',
            },
          ],
        },
        {
          id: 'DE',
          label: 'Germany',
          children: [
            {
              id: 'BW',
              label: 'Baden-Württemberg',
            },
            {
              id: 'BY',
              label: 'Bavaria',
            },
            {
              id: 'BE',
              label: 'Berlin',
            },
            {
              id: 'BB',
              label: 'Brandenburg',
            },
            {
              id: 'HB',
              label: 'Bremen',
            },
            {
              id: 'HH',
              label: 'Hamburg',
            },
            {
              id: 'HE',
              label: 'Hesse',
            },
            {
              id: 'NI',
              label: 'Lower Saxony',
            },
            {
              id: 'MV',
              label: 'Mecklenburg-Western Pomerania',
            },
            {
              id: 'NW',
              label: 'North Rhine-Westphalia',
            },
            {
              id: 'RP',
              label: 'Rhineland-Palatinate',
            },
            {
              id: 'SL',
              label: 'Saarland',
            },
            {
              id: 'SN',
              label: 'Saxony',
            },
            {
              id: 'ST',
              label: 'Saxony-Anhalt',
            },
            {
              id: 'SH',
              label: 'Schleswig-Holstein',
            },
            {
              id: 'TH',
              label: 'Thuringia',
            },
          ],
        },
        {
          id: 'BE',
          label: 'Belgium',
          children: [
            {
              id: 'BRU',
              label: 'Brussels-Capital Region',
            },
            {
              id: 'VLG',
              label: 'Flemish Region',
            },
            {
              id: 'WAL',
              label: 'Walloon Region',
            },
          ],
        },
        {
          id: 'NL',
          label: 'Netherlands',
          children: [
            {
              id: 'DR',
              label: 'Drenthe',
            },
            {
              id: 'FL',
              label: 'Flevoland',
            },
            {
              id: 'FR',
              label: 'Friesland',
            },
            {
              id: 'GE',
              label: 'Gelderland',
            },
            {
              id: 'GR',
              label: 'Groningen',
            },
            {
              id: 'LI',
              label: 'Limburg',
            },
            {
              id: 'NB',
              label: 'North Brabant',
            },
            {
              id: 'NH',
              label: 'North Holland',
            },
            {
              id: 'OV',
              label: 'Overijssel',
            },
            {
              id: 'UT',
              label: 'Utrecht',
            },
            {
              id: 'ZE',
              label: 'Zeeland',
            },
            {
              id: 'ZH',
              label: 'South Holland',
            },
          ],
        },
      ];

      // Initialize default country and filtered country codes
      const {
        defaultCountry,
        filteredCountryCodes,
      } = this.processFilterCountries(this.filteredCountries);

      // Set default country code (if applicable)
      this.defaultCountryCode = defaultCountry?.code;

      // Set deliverable country options
      return options.filter((country) =>
        filteredCountryCodes.includes(country.id),
      );
    } catch (error) {
      this.wdErrorService.userError(
        'Error',
        `Error getting shipping country/state options`,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isLoading.next(false);
    }
  }
}
